import type {
  OocyteCohort,
  Specimen,
} from "@src/packages/care/cryovault/useSpecimens";
import { getRemainingOocyteCohortCount } from "@src/packages/care/cryovault/oocyteCohortInventory";
import { CONSUMING_SPECIMEN_USAGES } from "@src/packages/care/cryovault/frozenSpecimenUsages";
import type { Cycle, Phase } from "../hooks/useCycles";
import {
  resolvePostProcedureOutcomeClinicalState,
  type PostProcedureOutcomeClinicalState,
} from "./postProcedureOutcomeState";

const OBSERVED_RESULT_ORDER = [
  "retrieved_oocytes",
  "m2_count",
  "fertilized_oocytes",
  "embryos_day_3",
  "embryos_day_5",
  "frozen_embryo",
  "transferred_embryo",
] as const;

export type ObservedResultMetric = {
  metric: (typeof OBSERVED_RESULT_ORDER)[number] | string;
  value: number;
  phaseId: string;
  metricId: string | null;
};

type ApiFertilityMetric = NonNullable<Phase["fertilityMetrics"]>[number];
type ObservedResultsPhase = Pick<Phase, "id"> & {
  fertilityMetrics?:
    | readonly Partial<Pick<ApiFertilityMetric, "id" | "type" | "value">>[]
    | null;
};

/**
 * Read the metrics that the API actually recorded for a cycle.
 *
 * A missing metric is deliberately omitted; it is not equivalent to zero.
 * The phase and metric IDs stay attached so callers can explain where a
 * displayed value came from and future corrections can target the source row.
 */
export function extractObservedResults(
  phases: readonly ObservedResultsPhase[],
): ObservedResultMetric[] {
  const metrics = phases.flatMap((phase) =>
    (phase.fertilityMetrics ?? []).map((metric) => ({ phase, metric })),
  );

  return OBSERVED_RESULT_ORDER.flatMap((metricType) => {
    const match = metrics.find(
      ({ metric }) => metric.type === metricType && metric.value != null,
    );
    if (!match || match.metric.value == null) return [];

    return [
      {
        metric: metricType,
        value: match.metric.value,
        phaseId: match.phase.id,
        metricId: match.metric.id ?? null,
      },
    ];
  });
}

export type PostCycleLifecycleState =
  | "in_progress"
  | "results_recorded_open"
  | "completed_preservation"
  | "completed_with_outcome"
  | "closed_unspecified"
  | "cancelled";

export type PostCycleLifecycle = {
  state: PostCycleLifecycleState;
  status: Cycle["status"];
  cycleType: Cycle["cycleType"];
  clinicalState: PostProcedureOutcomeClinicalState;
  outcomeType: NonNullable<Cycle["outcome"]>["type"] | null;
  resultStatus: NonNullable<Cycle["outcome"]>["resultStatus"] | null;
  recordStatus: NonNullable<Cycle["outcome"]>["recordStatus"] | null;
  verificationStatus:
    NonNullable<Cycle["outcome"]>["verificationStatus"] | null;
  acknowledgedAt: string | null;
};

const PRESERVATION_CYCLE_TYPES = new Set<Cycle["cycleType"]>([
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

// Egg and embryo freezing expose a source-backed final quantity through the
// frozen_embryo metric (the API uses that metric for eggs frozen as well).
// Do not turn a closed cycle into a completed preservation cycle until that
// final result exists; retrieved or mature counts are not the preservation
// outcome.
const PRESERVATION_CYCLE_TYPES_REQUIRING_FINAL_RESULT = new Set<
  Cycle["cycleType"]
>(["egg_freezing", "embryo_freezing"]);

/**
 * Resolve lifecycle without making a clinical decision from inventory.
 * Outcome meaning is delegated to the canonical post-procedure resolver;
 * acknowledgement, verification, and record status remain visible metadata.
 */
export function resolvePostCycleLifecycle(
  cycle: Pick<Cycle, "status" | "cycleType"> & {
    outcome: Pick<
      NonNullable<Cycle["outcome"]>,
      | "type"
      | "resultStatus"
      | "recordStatus"
      | "verificationStatus"
      | "acknowledgedAt"
    > | null;
  },
  observedResultCount = 0,
  finalPreservationResultRecorded = false,
): PostCycleLifecycle {
  const outcome = cycle.outcome;
  const clinicalState = resolvePostProcedureOutcomeClinicalState({
    type: outcome?.type,
    resultStatus: outcome?.resultStatus,
    recordStatus: outcome?.recordStatus,
    verificationStatus: outcome?.verificationStatus,
  });

  let state: PostCycleLifecycleState;
  if (clinicalState === "cancelled") {
    state = "cancelled";
  } else if (cycle.status === "open") {
    state = observedResultCount > 0 ? "results_recorded_open" : "in_progress";
  } else if (
    clinicalState === "pregnancy" ||
    clinicalState === "no_pregnancy" ||
    clinicalState === "adverse"
  ) {
    state = "completed_with_outcome";
  } else if (
    PRESERVATION_CYCLE_TYPES.has(cycle.cycleType) &&
    (!PRESERVATION_CYCLE_TYPES_REQUIRING_FINAL_RESULT.has(cycle.cycleType) ||
      finalPreservationResultRecorded)
  ) {
    state = "completed_preservation";
  } else {
    state = "closed_unspecified";
  }

  return {
    state,
    status: cycle.status,
    cycleType: cycle.cycleType,
    clinicalState,
    outcomeType: outcome?.type ?? null,
    resultStatus: outcome?.resultStatus ?? null,
    recordStatus: outcome?.recordStatus ?? null,
    verificationStatus: outcome?.verificationStatus ?? null,
    acknowledgedAt: outcome?.acknowledgedAt ?? null,
  };
}

type InventorySpecimen = Pick<
  Specimen,
  | "id"
  | "type"
  | "status"
  | "cycleId"
  | "verificationStatus"
  | "storageLocations"
  | "specimenUsages"
>;
type InventoryCohort = Pick<
  OocyteCohort,
  | "id"
  | "originCycleId"
  | "status"
  | "numberFrozen"
  | "numberThawed"
  | "verificationStatus"
  | "members"
>;

export type PostCycleInventoryScope =
  | { kind: "vault" }
  | { kind: "origin_cycle"; cycleId: string }
  | { kind: "consuming_cycle"; cycleId: string };

export type PostCycleInventorySnapshot = {
  counts: Record<Specimen["type"], number>;
  specimenIds: string[];
  cohortIds: string[];
  unverifiedCount: number;
  unknownLinkage: boolean;
};

export type PostCycleSectionState =
  "loading" | "error" | "partial" | "empty" | "unknown_linkage" | "ready";

export function resolvePostCycleSectionState({
  isLoading,
  isError,
  hasData,
  hasUnknownLinkage = false,
}: {
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  hasUnknownLinkage?: boolean;
}): PostCycleSectionState {
  if (isLoading && hasData) return "partial";
  if (isError && hasData) return "partial";
  if (isError) return "error";
  if (isLoading) return "loading";
  if (hasUnknownLinkage) return "unknown_linkage";
  if (!hasData) return "empty";
  return "ready";
}

const EMPTY_COUNTS: Record<Specimen["type"], number> = {
  semen: 0,
  oocyte: 0,
  embryo: 0,
};

function isStoredVerifiedSpecimen(specimen: InventorySpecimen): boolean {
  return (
    specimen.status === "stored" &&
    specimen.verificationStatus === "verified" &&
    specimen.storageLocations.some(
      (location) => location.isActive && location.storageType === "frozen",
    )
  );
}

function matchesSpecimenScope(
  specimen: InventorySpecimen,
  scope: PostCycleInventoryScope,
): boolean {
  if (scope.kind === "vault") return true;
  if (scope.kind === "origin_cycle") return specimen.cycleId === scope.cycleId;

  return specimen.specimenUsages.some(
    (usage) =>
      usage.cycleId === scope.cycleId &&
      CONSUMING_SPECIMEN_USAGES.has(usage.usage),
  );
}

function matchesCohortScope(
  cohort: InventoryCohort,
  scope: PostCycleInventoryScope,
): boolean {
  if (scope.kind === "vault") return true;
  if (scope.kind === "origin_cycle") {
    return cohort.originCycleId === scope.cycleId;
  }
  return cohort.members.some((member) => member.cycleId === scope.cycleId);
}

/**
 * Build a reconciled inventory snapshot. Cohort totals are authoritative for
 * oocytes; member rows are linkage evidence and are never counted twice.
 */
export function buildPostCycleInventorySnapshot(
  specimens: readonly InventorySpecimen[] | undefined,
  cohorts: readonly InventoryCohort[] | undefined,
  scope: PostCycleInventoryScope,
): PostCycleInventorySnapshot {
  const counts = { ...EMPTY_COUNTS };
  const specimenIds: string[] = [];
  const cohortIds: string[] = [];
  const cohortMemberIds = new Set(
    (cohorts ?? []).flatMap((cohort) =>
      cohort.members.map((member) => member.id),
    ),
  );
  let unverifiedCount = 0;
  let unknownLinkage = false;

  for (const cohort of cohorts ?? []) {
    if (!matchesCohortScope(cohort, scope)) continue;
    if (cohort.verificationStatus !== "verified") {
      unverifiedCount += 1;
      continue;
    }
    if (cohort.status !== "stored" && cohort.status !== "partially_used") {
      continue;
    }

    const remaining = getRemainingOocyteCohortCount(
      cohort.numberFrozen,
      cohort.numberThawed,
      cohort.members.length,
    );
    if (remaining > 0) {
      counts.oocyte += remaining;
      cohortIds.push(cohort.id);
    }
  }

  for (const specimen of specimens ?? []) {
    if (!matchesSpecimenScope(specimen, scope)) continue;
    if (!isStoredVerifiedSpecimen(specimen)) {
      unverifiedCount += 1;
      continue;
    }
    if (cohortMemberIds.has(specimen.id)) continue;

    counts[specimen.type] += 1;
    specimenIds.push(specimen.id);
  }

  if (scope.kind === "consuming_cycle") {
    const hasAvailableCohort = (cohorts ?? []).some(
      (cohort) =>
        cohort.status === "stored" || cohort.status === "partially_used",
    );
    const hasLinkedInventory = specimenIds.length > 0;
    unknownLinkage = hasAvailableCohort && !hasLinkedInventory;
  }

  return {
    counts,
    specimenIds,
    cohortIds,
    unverifiedCount,
    unknownLinkage,
  };
}
